
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Copy, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { PixelScriptMetadata } from "../UpdatePixelScript";
import { ScriptFormatter } from "./components/ScriptFormatter";
import { InstallationGuide } from "./components/InstallationGuide";
import { ScriptDisplay } from "./components/ScriptDisplay";
import { AdvancedOptions } from "./components/AdvancedOptions";
import { PrivacyOptions } from "./components/PrivacyOptions";
import { AdminControls } from "./components/AdminControls";

interface InstallationCodeProps {
  pixelId: string;
}

export function InstallationCode({ pixelId }: InstallationCodeProps) {
  const [copied, setCopied] = useState(false);
  const [scriptMetadata, setScriptMetadata] = useState<PixelScriptMetadata | null>(null);

  const { data: isAdmin } = useQuery({
    queryKey: ['user-role-admin'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('has_role', {
        _role: 'admin'
      });
      if (error) throw error;
      return !!data;
    }
  });

  useEffect(() => {
    async function fetchScriptMetadata() {
      try {
        const { data, error } = await supabase.storage
          .from('static')
          .download('pixel.metadata.json');
          
        if (error) throw error;
        
        if (data) {
          const text = await data.text();
          const metadata = JSON.parse(text) as PixelScriptMetadata;
          setScriptMetadata(metadata);
        }
      } catch (error) {
        console.error('Error fetching script metadata:', error);
      }
    }
    
    fetchScriptMetadata();
  }, []);

  const trackingScript = ScriptFormatter.getTrackingScript(pixelId, scriptMetadata);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(trackingScript);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Tracking code copied to clipboard");
    } catch (err) {
      toast.error("Failed to copy to clipboard");
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-medium">1. Add this code to your website</h3>
      
      <InstallationGuide />
      
      <ScriptDisplay script={trackingScript} />
      
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          className="flex-1"
          onClick={copyToClipboard}
        >
          {copied ? (
            <>
              <Check className="w-4 h-4 mr-2" />
              Copied!
            </>
          ) : (
            <>
              <Copy className="w-4 h-4 mr-2" />
              Copy Code
            </>
          )}
        </Button>
        
        <AdminControls 
          isAdmin={isAdmin} 
          onScriptUpdate={setScriptMetadata} 
        />
      </div>

      <AdvancedOptions 
        pixelId={pixelId} 
        scriptMetadata={scriptMetadata} 
      />
      
      <PrivacyOptions 
        pixelId={pixelId} 
        scriptMetadata={scriptMetadata} 
      />
    </div>
  );
}
